import { DropDown, TextInput } from '@zappyrent/hundop';
import { ITextInputProps } from '@zappyrent/hundop/build/components/TextInput/TextInput.types';
import { camelCase, upperFirst } from 'lodash';
import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import shallow from 'zustand/shallow';

import {
  StyledH2,
  StyledNarrowWrapper,
} from '../../../pages/UploadDocuments/UploadDocuments.styles';
import {
  UPLOAD_DOCUMENTS_FLOW_STEPS,
  USERS_TYPES,
} from '../../../shared/constants';
import useBookingData from '../../../shared/hooks/useBookingData';
import useStore from '../../../store';
import UploadDocumentsActionBar from '../ActionBar';
import { StyledSpinner, StyledButton } from './AddAGuarantor.styles';

const AddAGuarantor = (): JSX.Element => {
  const { t } = useTranslation(['form', 'documentsflow', 'routes']);

  const navigate = useNavigate();
  const { locale } = useParams();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');

  const { user, guarantors, postGuarantor } = useStore(
    state => ({
      user: state.user,
      guarantors: state.guarantors,
      postGuarantor: state.postGuarantor,
    }),
    shallow,
  );

  const bookingData = bookingId ? useBookingData(bookingId) : null;

  const [isLoadingThroughFirstButton, setIsLoadingThroughFirstButton] =
    useState(false);
  const [isLoadingThroughSecondButton, setIsLoadingThroughSecondButton] =
    useState(false);

  const [firstNameInput, setFirstNameInput] = useState({
    id: 'first-name',
    name: 'first-name',
    type: 'text',
    label: upperFirst(t('form:firstName')),
    value: '',
  });
  const [lastNameInput, setLastNameInput] = useState({
    id: 'last-name',
    name: 'last-name',
    type: 'text',
    label: upperFirst(t('form:lastName')),
    value: '',
  });
  const [occupationSelect, setOccupationSelect] = useState({
    id: 'occupation',
    name: 'occupation',
    label: upperFirst(t('form:occupation')),
    options: [
      {
        text: upperFirst(t('form:italianStudent')),
        value: USERS_TYPES.ITALIAN_STUDENT,
      },
      {
        text: upperFirst(t('form:foreignStudent')),
        value: USERS_TYPES.FOREIGN_STUDENT,
      },
      {
        text: upperFirst(t('form:selfEmployed')),
        value: USERS_TYPES.SELF_EMPLOYED,
      },
      {
        text: upperFirst(t('form:employee')),
        value: USERS_TYPES.EMPLOYEE,
      },
    ],
    value: '',
  });

  const areInputsFilled =
    firstNameInput.value && lastNameInput.value && occupationSelect.value;

  const resetInputValues = () => {
    setFirstNameInput(prevFirstNameInput => ({
      ...prevFirstNameInput,
      value: '',
    }));
    setLastNameInput(prevLastNameInput => ({
      ...prevLastNameInput,
      value: '',
    }));
    setOccupationSelect(prevOccupationSelect => ({
      ...prevOccupationSelect,
      value: '',
    }));
  };

  const handleTextInputChange = (
    event: ChangeEvent<HTMLInputElement>,
    setInput: Dispatch<
      SetStateAction<{
        label: string;
        id: string;
        name: string;
        type: string;
        value: string;
      }>
    >,
  ) => {
    setInput(prevInput => ({
      ...prevInput,
      value: event.target.value,
    }));
  };

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setOccupationSelect(prevSelect => ({
      ...prevSelect,
      value: event.target.value,
    }));
  };

  const handleOnAddGuarantor = async (isMyself: boolean) => {
    const firstName = isMyself ? user!.first_name : firstNameInput.value;
    const lastName = isMyself ? user!.last_name : lastNameInput.value;
    const occupation = isMyself
      ? camelCase(bookingData!.tenant_reason!)
      : occupationSelect.value;

    const existingGuarantor = guarantors.find(
      guarantor =>
        guarantor.first_name.toLowerCase() === firstName.toLowerCase() &&
        guarantor.last_name.toLowerCase() === lastName.toLowerCase() &&
        guarantor.occupation === occupation,
    );

    let guarantorId = existingGuarantor?.id;

    if (!existingGuarantor) {
      guarantorId = await postGuarantor(
        bookingId!,
        firstName,
        lastName,
        occupation,
        isMyself,
      );
    }

    navigate(
      `/${locale}/${t('routes:uploadDocuments')}/${
        UPLOAD_DOCUMENTS_FLOW_STEPS.UPLOAD_GUARANTOR_DOCUMENTS
      }?booking_id=${bookingId}&guarantor_id=${guarantorId}`,
      {
        state: {
          isUserTheGuarantor: isMyself,
        },
      },
    );

    resetInputValues();
  };

  return (
    <>
      <StyledNarrowWrapper>
        <StyledH2>{upperFirst(t('documentsflow:addAGuarantor'))}</StyledH2>
        <StyledButton
          onClick={() => {
            setIsLoadingThroughFirstButton(true);
            handleOnAddGuarantor(true);
          }}
        >
          {upperFirst(t('documentsflow:iCanContributeMyself'))}
          {isLoadingThroughFirstButton && <StyledSpinner />}
        </StyledButton>
        <TextInput
          id={firstNameInput.name}
          label={firstNameInput.label}
          name={firstNameInput.name}
          type={firstNameInput.type as ITextInputProps['type']}
          value={firstNameInput.value as string}
          onChange={event => handleTextInputChange(event, setFirstNameInput)}
        />
        <TextInput
          id={lastNameInput.name}
          label={lastNameInput.label}
          name={lastNameInput.name}
          type={lastNameInput.type as ITextInputProps['type']}
          value={lastNameInput.value as string}
          onChange={event => handleTextInputChange(event, setLastNameInput)}
        />
        <DropDown
          id={occupationSelect.id}
          name={occupationSelect.name}
          label={occupationSelect.label}
          options={occupationSelect.options}
          value={occupationSelect.value}
          onChange={handleSelectChange}
        />
      </StyledNarrowWrapper>
      <UploadDocumentsActionBar
        isLoading={isLoadingThroughSecondButton}
        isContinueButtonDisabled={!areInputsFilled}
        onContinue={() => {
          setIsLoadingThroughSecondButton(true);
          handleOnAddGuarantor(false);
        }}
      />
    </>
  );
};

export default AddAGuarantor;
